import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ContentCompetency, competencyTransform } from '@backrs_ui/common-ui';

import { HoverButton } from '@backrs_ui/common-ui/src/molecules/Nugget/HoverButton';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
  usePostBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, SxProps, Theme } from '@mui/material';

type CompetencyBannerProps = Readonly<{
  contentCompetency: ContentCompetency;
}>;

export const CompetencyBanner = ({ contentCompetency }: CompetencyBannerProps) => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const [isHovered, setIsHovered] = useState(false);

  const bookmarkId = getBookmarkIdForNugget(bookmarks, contentCompetency.id, 'competency');
  const isBookmarked = !!bookmarkId;

  const handleBookmark = useCallback(async () => {
    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({ type: 'competency', itemId: contentCompetency.id });
    }
  }, [bookmarkId, deleteBookmark, postBookmark, contentCompetency.id]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${GROW_URL}${pathname}`);
    } catch (error) {
      console.error('Failed to copy link to clipboard:', error);
    }
  }, [pathname]);

  const handleAddTask = useCallback(() => {
    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(competencyTransform(contentCompetency)));
  }, [dispatch, contentCompetency]);

  if (!contentCompetency.media_url) return null;

  return (
    <Box
      sx={bannerImageWrapperSx}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        component="img"
        src={contentCompetency.media_url}
        alt={contentCompetency.title}
        sx={bannerImageSx}
      />
      <HoverButton
        isVisible={isHovered}
        idPrefix="competency-banner"
        hasBookmarked={isBookmarked}
        handleActionBookmark={handleBookmark}
        handleActionShare={handleShare}
        handleActionAddTask={handleAddTask}
      />
    </Box>
  );
};

const bannerImageWrapperSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

const bannerImageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  width: '100%',
  height: { xs: '140px', sm: '200px' },
  objectFit: 'cover',
  objectPosition: 'center',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});
